"use client";

import Image from "next/image";
import Link from "next/link";
import { Facebook, Instagram, Sparkles } from "lucide-react";
import { FOOTER_APPROACHES } from "@/data/approaches";

export default function Footer() {
  const openCookieSettings = () => {
    const klaro = (window as Window & { klaro?: { show?: () => void } }).klaro;
    klaro?.show?.();
  };

  return (
    <footer className="no-hashtag-zone relative overflow-hidden bg-[var(--brand-charcoal)] pt-16 pb-10 text-white">
      <div className="relative z-10 max-w-7xl mx-auto px-6 grid gap-10 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
        {/* Logo & Contact */}
        <div className="space-y-4 md:col-span-1">
          <Link href="/" className="inline-block">
            <Image
              src="/logo-white.svg"
              alt="Mindtale"
              width={120}
              height={40}
              className="object-contain"
            />
          </Link>

          <p className="max-w-xs text-sm leading-relaxed text-white/80">
            Terapie online care dă mai departe. Fiecare ședință Mindtale
            susține accesul la sănătate mintală pentru toți.
          </p>

          {/* Legal entity tied to the Mindtale brand. Stated explicitly and
              visibly so Google Ads can verify the business-name asset
              ("Zenyra Therapy SRL") against the landing page. */}
          <p className="max-w-xs text-sm leading-relaxed text-white/80">
            Mindtale este o marcă a <strong>Zenyra Therapy SRL</strong>.
          </p>

          <Link
            href="mailto:hello@mindtale.ro"
            className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-white/90 hover:text-white hover:underline"
          >
            hello@mindtale.ro
          </Link>

          <div className="flex gap-4 pt-3">
            <Link
              href="https://www.facebook.com/mindtale.ro"
              target="_blank"
              className="rounded-md bg-white/10 p-2 transition hover:bg-white/20"
              aria-label="Facebook"
            >
              <Facebook size={18} />
            </Link>
            <Link
              href="https://www.instagram.com/mindtale.ro/"
              target="_blank"
              className="rounded-md bg-white/10 p-2 transition hover:bg-white/20"
              aria-label="Instagram"
            >
              <Instagram size={18} />
            </Link>
          </div>
        </div>

        {/* Despre terapie */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--brand-saffron)]">
            Despre terapie
          </h2>
          <ul className="space-y-2 text-sm">
            <li>
              <Link
                href="/zenyra"
                className="inline-flex items-center gap-1.5 font-semibold text-[var(--brand-saffron)] hover:underline"
              >
                <Sparkles className="size-3" />
                Sesiune cu Zenyra
              </Link>
            </li>
            <li>
              <Link href="/psihoterapeut-online" className="text-white/85 hover:text-white hover:underline">
                Psihoterapeut online
              </Link>
            </li>
            <li>
              <Link href="/ce-este-psihoterapia" className="text-white/85 hover:text-white hover:underline">
                Ce este psihoterapia?
              </Link>
            </li>
            <li>
              <Link href="/prima-sedinta" className="text-white/85 hover:text-white hover:underline">
                Prima ședință de terapie
              </Link>
            </li>
            <li>
              <Link href="/cat-costa" className="text-white/85 hover:text-white hover:underline">
                Cât costă terapia?
              </Link>
            </li>
            <li>
              <Link href="/psiholog-psihoterapeut" className="text-white/85 hover:text-white hover:underline">
                Psiholog vs. psihoterapeut
              </Link>
            </li>
            <li>
              <Link href="/fondul-social" className="text-white/85 hover:text-white hover:underline">
                Fondul Social Mindtale
              </Link>
            </li>
            <li>
              <Link href="/resurse" className="text-white/85 hover:text-white hover:underline">
                Toate resursele
              </Link>
            </li>
            <li>
              <Link href="/blog" className="text-white/85 hover:text-white hover:underline">
                Blog
              </Link>
            </li>
            <li>
              <Link href="/teste" className="text-white/85 hover:text-white hover:underline">
                Teste validate clinic
              </Link>
            </li>
          </ul>
        </div>

        {/* Stări frecvente */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--brand-saffron)]">
            Stări frecvente
          </h2>
          <ul className="space-y-2 text-sm">
            <li>
              <Link href="/anxietate" className="text-white/85 hover:text-white hover:underline">
                Anxietate
              </Link>
            </li>
            <li>
              <Link href="/depresie" className="text-white/85 hover:text-white hover:underline">
                Depresie
              </Link>
            </li>
            <li>
              <Link href="/burnout" className="text-white/85 hover:text-white hover:underline">
                Burnout & Epuizare
              </Link>
            </li>
            <li>
              <Link href="/tulburare-de-panica" className="text-white/85 hover:text-white hover:underline">
                Tulburare de panică
              </Link>
            </li>
            <li>
              <Link href="/trauma-si-ptsd" className="text-white/85 hover:text-white hover:underline">
                Traumă și PTSD
              </Link>
            </li>
            <li>
              <Link href="/relatii-si-cuplu" className="text-white/85 hover:text-white hover:underline">
                Relații și cuplu
              </Link>
            </li>
            <li>
              <Link href="/terapie-pentru-copii-si-adolescenti" className="text-white/85 hover:text-white hover:underline">
                Copii și adolescenți
              </Link>
            </li>
            <li>
              <Link href="/insomnie" className="text-white/85 hover:text-white hover:underline">
                Insomnie
              </Link>
            </li>
            <li>
              <Link href="/doliu" className="text-white/85 hover:text-white hover:underline">
                Doliu
              </Link>
            </li>
            <li>
              <Link href="/stres" className="text-white/85 hover:text-white hover:underline">
                Stres
              </Link>
            </li>
            <li>
              <Link href="/tulburare-bipolara" className="text-white/85 hover:text-white hover:underline">
                Tulburare bipolară
              </Link>
            </li>
            <li>
              <Link href="/tulburare-borderline" className="text-white/85 hover:text-white hover:underline">
                Tulburare borderline
              </Link>
            </li>
            <li>
              <Link href="/procrastinare" className="text-white/85 hover:text-white hover:underline">
                Procrastinare
              </Link>
            </li>
            <li>
              <Link href="/resurse" className="text-white/85 hover:text-white hover:underline">
                Toate stările →
              </Link>
            </li>
          </ul>
        </div>

        {/* Abordări terapeutice */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--brand-saffron)]">
            Abordări terapeutice
          </h2>
          <ul className="space-y-2 text-sm">
            {FOOTER_APPROACHES.map((approach) => (
              <li key={approach.slug}>
                <Link
                  href={approach.slug}
                  className="text-white/85 hover:text-white hover:underline"
                >
                  {approach.short}
                </Link>
              </li>
            ))}
            <li>
              <Link href="/resurse" className="text-white/85 hover:text-white hover:underline">
                Toate abordările
              </Link>
            </li>
          </ul>
        </div>

        {/* Companii & Colaborare */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--brand-saffron)]">
            Companii & Colaborare
          </h2>
          <ul className="space-y-2 text-sm">
            <li>
              <Link href="/burnout" className="text-white/85 hover:text-white hover:underline">
                Burnout la locul de muncă
              </Link>
            </li>
            <li>
              <Link href="/colaborare/organizatii" className="text-white/85 hover:text-white hover:underline">
                Pentru organizații
              </Link>
            </li>
            <li>
              <Link href="/colaborare/terapeuti" className="text-white/85 hover:text-white hover:underline">
                Pentru psihoterapeuți
              </Link>
            </li>
            <li>
              <Link href="/despre-noi" className="text-white/85 hover:text-white hover:underline">
                Despre noi
              </Link>
            </li>
            <li>
              <Link href="/contact" className="text-white/85 hover:text-white hover:underline">
                Contact
              </Link>
            </li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-[var(--brand-saffron)]">
            Legal
          </h2>
          <ul className="space-y-2 text-sm">
            <li>
              <Link href="/termeni-si-conditii" className="text-white/85 hover:text-white hover:underline">
                Termeni și condiții
              </Link>
            </li>
            <li>
              <Link href="/politica-gdpr" className="text-white/85 hover:text-white hover:underline">
                Politică GDPR
              </Link>
            </li>
            <li>
              <Link href="/politica-cookie" className="text-white/85 hover:text-white hover:underline">
                Politică cookie-uri
              </Link>
            </li>
            <li>
              <button
                type="button"
                onClick={openCookieSettings}
                className="text-white/85 hover:text-white hover:underline"
              >
                Setări cookie
              </button>
            </li>
          </ul>
        </div>
      </div>

      {/* Divider */}
      <div className="mt-12 mb-6 border-t border-white/20" />

      {/* Bottom legal row */}
      <div className="relative z-10 mx-auto flex max-w-7xl flex-col items-center justify-between gap-6 px-6 text-center text-xs text-white/70 md:flex-row md:text-left">
        <p>
          © {new Date().getFullYear()} Zenyra Therapy SRL. Toate drepturile rezervate.
        </p>

        <p className="max-w-lg leading-relaxed text-center md:text-left">
          Dacă starea ta emoțională este o amenințare la adresa ta sau a celor
          din jur, te rugăm să suni la <strong>112</strong> sau să contactezi
          cel mai apropiat serviciu de urgență. Mindtale nu este un serviciu de urgență.
        </p>

        {/* <div className="flex items-center gap-3">
          <Image
            src="/payments/visa.svg"
            alt="Visa"
            width={40}
            height={20}
            className="opacity-80"
          />
          <Image
            src="/payments/mastercard.svg"
            alt="Mastercard"
            width={40}
            height={20}
            className="opacity-80"
          />
          <Image
            src="/payments/gpay.svg"
            alt="Google Pay"
            width={40}
            height={20}
            className="opacity-80"
          />
          <Image
            src="/payments/applepay.svg"
            alt="Apple Pay"
            width={40}
            height={20}
            className="opacity-80"
          />
        </div> */}
      </div>

      {/* ANPC row */}
      <div className="mx-auto mt-8 flex max-w-7xl flex-col items-center justify-center gap-4 px-6 text-xs text-white/70 sm:flex-row">
        <Image
          src="/anpc-sal.webp"
          alt="ANPC SAL"
          width={120}
          height={40}
          className="object-contain"
        />
        <Image
          src="/anpc-sol.webp"
          alt="ANPC SOL"
          width={120}
          height={40}
          className="object-contain"
        />
      </div>
    </footer>
  );
}
